'use client'
import { useSearchType } from '@/api/hooks'
import { PageContext } from '@/app/(site)/header-init'
import { processSearchTerm } from '@/app/util/search-term'
import { TakeoverData } from '@/app/util/searchbar-takeover'
import { useIsMobile } from '@/app/util/use-media-query'
import { AdProductContext } from '@/context/ad-product'
import FavoritesContext from '@/context/favorites-manager'
import { SearchbarContext } from '@/context/searchbar-manager'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { useClientSideAPIKey } from '@/util/url'
import { giphyBlack } from '@giphy/colors'
import { SearchBar as SearchBar_, SearchContext, SearchContextManager } from '@giphy/react-components'
import { setGADataLayer } from 'analytics'
import dynamic from 'next/dynamic'
import { useParams, useRouter, usePathname } from 'next/navigation'
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import Header, { desktopHeaderWidth } from 'ui/src/components/header'
import { belowHeaderZIndex, desktopWidth } from 'ui/src/constants'
import { desktop } from 'ui/src/css'
import { removeImageTypes } from 'utils/src/media'
import { Interface } from '../../styles/fonts'
import AnimatedLogo from '../logo'
import { DesktopNav } from '../nav/desktop/desktop'
import { desktopSearchbarPadding, mobileSearchbarHeight, searchbarHeight } from './constants'
import Takeover from './takeover'

const SearchDropdown = dynamic(() => import('./search-dropdown'), {})
const BodyScrollLock = dynamic(() => import('./body-scroll-lock'), {})
const OpenInAppCTA = dynamic(() => import('../../components/open-in-app'))

const isTakeoverTransparent = false
const takeoverInputSize = isTakeoverTransparent ? 590 : 540
const searchbarShrunkWidth = isTakeoverTransparent ? 400 : 360
const SearchBarComponent = styled(SearchBar_)<{ $hasTakeover?: boolean; $isStuck: boolean }>`
    input {
        font-size: 22px;
        padding-left: 14px;
        font-family: ${Interface.style.fontFamily};
        ${(props) =>
            props.$hasTakeover &&
            css`
                width: ${props.$isStuck ? searchbarShrunkWidth : takeoverInputSize}px;
                flex: unset;
            `}
    }
    ${(props) =>
        props.$hasTakeover &&
        css`
            /* separate the now shorter input and the right magnifying glass */
            justify-content: space-between;
        `}
    .giphy-search-bar-cancel {
        width: 22px;
        height: 22px;
        margin-right: 10px;
        ${(props) =>
            props.$hasTakeover &&
            css`
                position: absolute;
                left: ${takeoverInputSize}px;
                margin: 0;
            `}
    }
`

const SearchbarContainer = styled.div`
    padding: 5px 0;
    ${desktop(css`
        padding: ${desktopSearchbarPadding}px 0 0;
    `)}
`

const AnimationContainer = styled.div`
    background-color: ${giphyBlack};
    width: 100%;
    z-index: ${belowHeaderZIndex};
    position: sticky;
    /* this -1px lets us know if we're offscreen to the top and "stuck" */
    top: -1px;
`

const Container = styled.div<{ $isStuck?: boolean }>`
    width: 100%;
    ${desktop(css`
        transition: width 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) 0s;
        margin-left: auto;
    `)}
    ${({ $isStuck }) =>
        $isStuck &&
        desktop(css`
            width: calc(100% - ${desktopHeaderWidth}px);
            padding-bottom: 8px;
        `)}
`

type Props = {
    children?: ReactNode
    takeover?: TakeoverData
}

type BodyScrollLockContextProps = {
    ignoreTarget?: string
    setIgnoreTarget: (target: string) => void
}
export const BodyScrollLockContext = createContext({
    ignoreTarget: '',
    setIgnoreTarget: () => {},
} as BodyScrollLockContextProps)

const SearchInput = ({ isStuck }: { isStuck: boolean }) => {
    const { autoFocus } = useContext(SearchbarContext)
    const searchType = useSearchType()
    const { takeover } = useContext(AdProductContext)
    const placeholders = ['Search all the GIFs and Stickers', '@username to search channels']
    const [index, setIndex] = useState(0)
    const { activeChannel } = useContext(SearchContext)
    const router = useRouter()
    const pathname = usePathname()
    const isOnSearchPage = pathname?.startsWith('/search/')

    const onEnter = useCallback(
        (term: string) => {
            if (term) {
                const cleanTerm = encodeURIComponent(removeImageTypes(term).replace(/ /g, '-').replace('/', ''))
                setGADataLayer({
                    event: 'search',
                    options: {
                        searchType: 'search',
                        searchTerm: cleanTerm,
                    },
                })
                if (activeChannel) {
                    const channel = `@${activeChannel.slug.trim().toLowerCase()}`
                    router.push(`/search/?q=${channel}+${cleanTerm}`)
                } else {
                    // Check if type is explicitly mentioned in search term
                    const hasExplicitType = [
                        'gif',
                        'gifs',
                        'sticker',
                        'stickers',
                        'clip',
                        'clips',
                        'emoji',
                        'emojis',
                        'meme',
                        'memes',
                        'reaction',
                        'reactions',
                    ].some((typeWord) => cleanTerm.toLowerCase().includes(typeWord))

                    // Determine the type suffix to use
                    let typeSuffix = ''
                    if (hasExplicitType || (isOnSearchPage && searchType !== 'gifs')) {
                        // Use explicit type from search term, or preserve current type if on search page
                        typeSuffix = searchType === 'gifs' ? '' : `-${searchType}`
                    }

                    router.push(`/search/${cleanTerm}${typeSuffix}`)
                }
            }
        },
        [activeChannel, router, searchType, isOnSearchPage]
    )
    useEffect(() => {
        // for the placeholder animation
        const timeout = setTimeout(() => setIndex(index === 0 ? 1 : 0), 3000)
        return () => clearTimeout(timeout)
    }, [index])
    return (
        <SearchbarContainer>
            <SearchBarComponent
                $isStuck={isStuck}
                $hasTakeover={!!takeover}
                placeholder={placeholders[index]}
                onEnter={onEnter}
                autoFocus={autoFocus}
            />
            {takeover && <Takeover takeover={takeover} />}
        </SearchbarContainer>
    )
}
const PageContainer = styled.div`
    ${desktop(css`
        position: relative;
        width: ${desktopWidth}px;
        margin: 0 auto;
    `)}
`

const HeaderSearchBar = ({ children }: Props) => {
    const apiKey = useClientSideAPIKey()
    const router = useRouter()
    const { user } = useContext(UserContext)
    const { hideSearchbar } = useContext(PageContext)
    const { term: encodedTerm = '' } = useParams<{ term?: string }>()
    const [cleanTerm] = processSearchTerm(encodedTerm)
    const { favorites } = useContext(FavoritesContext)
    const [ignoreTarget, setIgnoreTarget] = useState('')
    const isSticky = useRef<HTMLDivElement | null>(null)
    const [isStuck, setIsStuck] = useState(false)

    useEffect(() => {
        let observer: IntersectionObserver
        if (isSticky.current) {
            observer = new IntersectionObserver(
                ([e]) => {
                    setIsStuck(e.intersectionRatio < 1)
                },
                {
                    threshold: [1],
                    // only observe the top of the search bar
                    rootMargin: `0px 1000px 1000px 1000px`,
                }
            )
            observer.observe(isSticky.current)
        }
        return () => observer?.disconnect()
    }, [])

    const isMobile = useIsMobile()

    return (
        <PageContainer>
            <BodyScrollLockContext.Provider value={{ setIgnoreTarget, ignoreTarget }}>
                <SearchContextManager
                    initialTerm={cleanTerm}
                    apiKey={apiKey}
                    shouldFetchChannels={false}
                    theme={{ searchbarHeight, mobileSearchbarHeight }}
                >
                    <BodyScrollLock />

                    {isMobile ? (
                        <Header
                            user={user}
                            onLogoClick={() => {
                                router.push('/')
                            }}
                            hasFavorites={favorites.length > 0}
                        />
                    ) : (
                        <>
                            <DesktopNav />
                            <div
                                className={cn(
                                    'absolute top-0 z-aboveHeader flex h-searchbarHeight items-center',
                                    isStuck &&
                                        'fixed top-[-70px] translate-y-[78px] transition-transform delay-300 duration-500 ease-[cubic-bezier(0.165,0.84,0.44,1)]'
                                )}
                            >
                                <AnimatedLogo />
                            </div>
                        </>
                    )}

                    {!hideSearchbar && (
                        <AnimationContainer ref={isSticky}>
                            <Container $isStuck={isStuck}>
                                <SearchInput isStuck={isStuck} />
                                <SearchDropdown isStuck={isStuck} />
                            </Container>
                        </AnimationContainer>
                    )}
                    {children}
                </SearchContextManager>
            </BodyScrollLockContext.Provider>
            <OpenInAppCTA />
        </PageContainer>
    )
}

export default HeaderSearchBar
